/**
 * Theme System for IntelligenceBank UI Primitives
 *
 * This theme has two types of colors:
 *
 * 1. DYNAMIC COLORS (6 tenant-specific) - Populated from login response:
 *    - colourPrimary       → colors.primary
 *    - colourPrimaryText   → colors.primaryText
 *    - colourSecondary     → colors.secondary
 *    - colourSecondaryText → colors.secondaryText
 *    - colourHighlight     → colors.highlight
 *    - colourHighlightText → colors.highlightText
 *
 * 2. FIXED TOKENS - Never change (success, error, grey scale, etc.)
 *
 * Typography uses a relative sizing system where all font sizes are
 * derived from a configurable base. Use createTheme() to customize:
 *
 * @example
 * // Default theme (14px base)
 * import { theme } from '@intelligencebank/connectingib';
 *
 * @example
 * // Accessibility mode (16px base - all text scales proportionally)
 * import { createTheme } from '@intelligencebank/connectingib';
 * const largeTheme = createTheme({ typography: { baseFontSize: 16 } });
 */

import {
  greyScale,
  greenColors,
  redColors,
  blueColors,
  orangeColors,
  textColors,
  borderColors,
  iconColors,
  backgroundColors,
  statusColors,
  snackbarColors,
  progressColors,
  transparentBlack,
  transparentWhite,
  defaultPaletteColors,
  ibUiShadows,
  borderRadius as ibUiBorderRadius,
} from './colors';
import {
  createFontSizes,
  DEFAULT_BASE_FONT_SIZE,
  FONT_SCALE_PRESETS,
  type TypographyConfig,
  type FontSizes,
  type FontScalePreset,
} from './typography';

/**
 * Grey scale - FIXED (never changes per tenant)
 */
const grey = {
  100: greyScale.grey100,
  150: greyScale.grey150,
  200: greyScale.grey200,
  300: greyScale.grey300,
  400: greyScale.grey400,
  500: greyScale.grey500,
  600: greyScale.grey600,
  700: greyScale.grey700,
  800: greyScale.grey800,
  900: greyScale.grey900,
} as const;

/**
 * Semantic color scales - FIXED (never changes per tenant)
 */
const semantic = {
  green: {
    lighter: greenColors.greenLighter,
    light: greenColors.greenLight,
    base: greenColors.greenBase,
    dark: greenColors.greenDark,
    darker: greenColors.greenDarker,
  },
  red: {
    lighter2: redColors.redLighter2,
    lighter: redColors.redLighter,
    light: redColors.redLight,
    base: redColors.redBase,
    dark: redColors.redDark,
    darker: redColors.redDarker,
  },
  blue: {
    lighter2: blueColors.blueLighter2,
    lighter: blueColors.blueLighter,
    light: blueColors.blueLight,
    base: blueColors.blueBase,
    dark: blueColors.blueDark,
    darker: blueColors.blueDarker,
    link: blueColors.blueLink,
  },
  orange: {
    lighter2: orangeColors.orangeLighter2,
    lighter: orangeColors.orangeLighter,
    light: orangeColors.orangeLight,
    base: orangeColors.orangeBase,
    dark: orangeColors.orangeDark,
    darker: orangeColors.orangeDarker,
  },
} as const;

export const productionTheme = {
  colors: {
    // =================================================================
    // DYNAMIC COLORS (6 tenant-specific, populated from login response)
    //
    // Login Response Field    →    Theme Property
    // ─────────────────────────────────────────────
    // colourPrimary           →    colors.primary
    // colourPrimaryText       →    colors.primaryText
    // colourSecondary         →    colors.secondary
    // colourSecondaryText     →    colors.secondaryText
    // colourHighlight         →    colors.highlight
    // colourHighlightText     →    colors.highlightText
    // =================================================================
    primary: defaultPaletteColors.PRIMARY_DEFAULT,
    primaryText: defaultPaletteColors.PRIMARY_TEXT_DEFAULT,
    secondary: defaultPaletteColors.SECONDARY_DEFAULT,
    secondaryText: defaultPaletteColors.SECONDARY_TEXT_DEFAULT,
    highlight: defaultPaletteColors.HIGHLIGHT_DEFAULT,
    highlightText: defaultPaletteColors.HIGHLIGHT_TEXT_DEFAULT,

    // =================================================================
    // FIXED TOKENS (never change per tenant)
    // =================================================================

    // Base colors
    white: '#FFFFFF',
    black: '#000000',

    // Text colors
    text: {
      primary: textColors.textColor,
      secondary: 'rgba(0, 0, 0, 0.7)',
      muted: '#949494',
      inverse: '#FFFFFF',
      fade: textColors.textColorFade,
      instruction: textColors.instructionColor,
      help: textColors.helpTextColor,
    },

    // Status colors
    status: {
      success: statusColors.successColor,
      error: statusColors.errorColor,
      warning: statusColors.warningColor,
      info: statusColors.infoColor,
      notice: statusColors.noticeColor,
    },

    // Snackbar colors
    snackbar: {
      success: snackbarColors.snackbarSuccessBackground,
      error: snackbarColors.snackbarErrorBackground,
      warning: snackbarColors.snackbarWarningBackground,
    },

    // Progress colors
    progress: {
      complete: progressColors.progressBarCompletedColor,
      error: progressColors.progressBarErrorColor,
    },

    // Background colors
    background: {
      default: '#FFFFFF',
      hover: '#efefef',
      active: '#e8e8e8',
      disabled: '#eee',
      processing: backgroundColors.backgroundProcessingColor,
      row: backgroundColors.rowGrey,
    },

    // Border colors
    border: {
      light: '#f5f5f5',
      medium: '#BFBFBF',
      input: '#949494',
      dotted: '#eee',
      grey: borderColors.borderGrey,
      shadow: borderColors.borderShadow,
    },

    // Icon colors
    icon: {
      grey: iconColors.iconGrey,
      disabled: iconColors.iconGreyDisabled,
      background: iconColors.iconBackground,
    },

    // Grey scale
    grey,

    // Semantic colors (green, red, blue, orange scales)
    semantic,

    // Transparency utilities
    transparent: {
      black50: transparentBlack.transparentBlack50,
      black20: transparentBlack.transparentBlack20,
      black08: transparentBlack.transparentBlack08,
      black05: transparentBlack.transparentBlack05,
      white50: transparentWhite.transparentWhite50,
      white20: transparentWhite.transparentWhite20,
      white05: transparentWhite.transparentWhite05,
    },
  },
  
  typography: {
    fontFamily: '"Roboto", sans-serif',
    fontFamilyIcons: '"Material Symbols Outlined"',
    
    // Base font size that all other sizes are derived from
    baseFontSize: DEFAULT_BASE_FONT_SIZE,
    
    // Font sizes calculated from baseFontSize using ratios
    fontSize: createFontSizes(DEFAULT_BASE_FONT_SIZE),
    
    fontWeight: {
      regular: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
    
    lineHeight: {
      tight: '1.25',
      normal: '1.5',
      relaxed: '20px',  // Production uses 20px for buttons
    },
  },
  
  spacing: {
    0: '0',
    px: '1px',
    1: '4px',
    2: '6px',
    3: '8px',
    4: '10px',
    5: '12px',
    6: '16px',
    7: '24px',
    8: '32px',
  },
  
  sizes: {
    buttonHeight: '36px',
    headerHeight: '48px',
    navHeight: '52px',
    thumbnailSmall: '96px',
    thumbnailLarge: '200px',
    iconSize: '24px',
  },
  
  borders: {
    radius: {
      sm: '4px',
      md: '8px',
      full: '9999px',
      // ib-ui naming convention
      small: ibUiBorderRadius.borderRadiusSmall,
      medium: ibUiBorderRadius.borderRadiusMedium,
      large: ibUiBorderRadius.borderRadiusLarge,
    },
    width: {
      thin: '1px',
      medium: '2px',
    },
  },
  
  shadows: {
    card: '0px 2px 8px 2px rgb(0 0 0 / 18%)',
    dropdown: '0px 2px 5px rgba(0, 0, 0, 0.25)',
    menu: '0 2px 8px rgba(0, 0, 0, 0.2)',
    snackbar: '0px 2px 5px rgb(0 0 0 / 25%)',
    // ib-ui shadows
    ibUi: {
      100: ibUiShadows.shadow100,
      200: ibUiShadows.shadow200,
      300: ibUiShadows.shadow300,
      footer: ibUiShadows.shadowFooter,
      selected: ibUiShadows.shadowSelected,
    },
  },
  
  transitions: {
    fast: 'opacity 0.2s, transform 0.1s',
    normal: '200ms',
    slow: '400ms',
    easing: {
      slide: 'cubic-bezier(0.17, 0.04, 0.03, 0.94)',
      easeOut: 'cubic-bezier(0, 0, 0.2, 1)',
    },
  },
  
  zIndex: {
    base: 0,
    header: 1,
    dropdown: 99,
    modal: 1000,
    snackbar: 10000,
  },
} as const;

export type Theme = typeof productionTheme;

/**
 * Theme configuration options for createTheme().
 */
export interface ThemeConfig {
  /**
   * Typography configuration options.
   */
  typography?: Partial<TypographyConfig>;
  
  /**
   * Dynamic tenant colors (from login API response).
   */
  colors?: {
    primary?: string;
    primaryText?: string;
    secondary?: string;
    secondaryText?: string;
    highlight?: string;
    highlightText?: string;
  };
}

/**
 * Creates a customized theme with the given configuration.
 * Use this to create themes with different base font sizes for accessibility,
 * or to apply tenant-specific colors from the login API response.
 *
 * @param config - Theme configuration options
 * @returns A complete theme object
 *
 * @example
 * // Default theme (same as importing `theme`)
 * const defaultTheme = createTheme();
 *
 * @example
 * // Large accessibility mode (16px base)
 * const largeTheme = createTheme({
 *   typography: { baseFontSize: 16 }
 * });
 *
 * @example
 * // With tenant colors from login response
 * const tenantTheme = createTheme({
 *   colors: {
 *     primary: loginResponse.colourPrimary,
 *     highlight: loginResponse.colourHighlight,
 *   }
 * });
 *
 * @example
 * // Using font scale presets
 * import { FONT_SCALE_PRESETS } from '@intelligencebank/connectingib';
 * const comfortableTheme = createTheme({
 *   typography: { baseFontSize: FONT_SCALE_PRESETS.comfortable }
 * });
 */
export function createTheme(config: ThemeConfig = {}): Theme {
  const baseFontSize = config.typography?.baseFontSize ?? DEFAULT_BASE_FONT_SIZE;
  
  return {
    ...productionTheme,
    colors: {
      ...productionTheme.colors,
      ...(config.colors?.primary && { primary: config.colors.primary }),
      ...(config.colors?.primaryText && { primaryText: config.colors.primaryText }),
      ...(config.colors?.secondary && { secondary: config.colors.secondary }),
      ...(config.colors?.secondaryText && { secondaryText: config.colors.secondaryText }),
      ...(config.colors?.highlight && { highlight: config.colors.highlight }),
      ...(config.colors?.highlightText && { highlightText: config.colors.highlightText }),
    },
    typography: {
      ...productionTheme.typography,
      baseFontSize,
      fontSize: createFontSizes(baseFontSize),
    },
  } as Theme;
}

// Default theme (backward compatible)
export const theme = productionTheme;

// Alias for API consistency
export { productionTheme as defaultTheme };

// Re-export ThemeProvider
export { ThemeProvider } from './ThemeProvider';
export type { ThemeProviderProps } from './ThemeProvider';

// Re-export typography utilities
export {
  createFontSizes,
  DEFAULT_BASE_FONT_SIZE,
  FONT_SCALE_PRESETS,
};
export type { TypographyConfig, FontSizes, FontScalePreset };

// Re-export colors module
export * from './colors';

// Re-export for convenience
export default productionTheme;
